import { useState, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from "recharts";

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function getFacultyCount(faculty, key) {
  const val = faculty[key];
  if (Array.isArray(val)) return val.length;
  if (typeof val === "number") return val;
  return 0;
}

export function MonthlyChart({ facultyList, darkMode: d, allSections }) {
  const now = new Date();
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth());

  const availableYears = useMemo(() => {
    const years = new Set();
    facultyList.forEach(f => {
      allSections.forEach(sec => {
        (f[sec.key] || []).forEach(entry => {
          if (entry.addedAt) {
            const y = new Date(entry.addedAt).getFullYear();
            if (!isNaN(y)) years.add(y);
          }
        });
      });
    });
    if (!years.size) years.add(now.getFullYear());
    return [...years].sort((a, b) => b - a);
  }, [facultyList, allSections, now]);

  const chartData = useMemo(() => {
    return allSections.map(sec => {
      let count = 0;
      facultyList.forEach(f => {
        (f[sec.key] || []).forEach(entry => {
          if (entry.addedAt) {
            const date = new Date(entry.addedAt);
            if (date.getFullYear() === selYear && date.getMonth() === selMonth) count++;
          }
        });
      });
      return { 
        name: sec.label, 
        shortName: sec.label.length > 10 ? sec.label.slice(0, 9) + "…" : sec.label, 
        count, 
        color: sec.color, 
        icon: sec.icon 
      };
    });
  }, [facultyList, selYear, selMonth, allSections]);

  const total = chartData.reduce((s, x) => s + x.count, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
      const item = payload[0].payload;
      return (
        <div style={{ background: "#fff", border: `1.5px solid ${item.color}`, borderRadius: 10, padding: "8px 14px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)" }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: item.color }}>{item.icon} {item.name}</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: "#0f2557" }}>{item.count} <span style={{ fontSize: 13, color: "#94a3b8", fontWeight: 600 }}>entries</span></div>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ background: d ? "rgba(255,255,255,0.04)" : "#fff", borderRadius: 20, padding: "22px 24px", boxShadow: "0 4px 24px rgba(37,99,235,0.08)", border: `1px solid ${d ? "rgba(255,255,255,0.08)" : "#e8f0fe"}`, height: "100%", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 22 }}>📊</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 900, color: d ? "#e2e8f0" : "#0f2557" }}>Monthly Activity</div>
            <div style={{ fontSize: 13, color: "#94a3b8" }}>Entries added per category</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <select value={selMonth} onChange={e => setSelMonth(Number(e.target.value))}
            style={{ background: d ? "#1e293b" : "#f1f5f9", color: d ? "#e2e8f0" : "#0f2557", border: "none", borderRadius: 8, padding: "5px 10px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
            {MONTH_NAMES.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
          <select value={selYear} onChange={e => setSelYear(Number(e.target.value))}
            style={{ background: d ? "#1e293b" : "#f1f5f9", color: d ? "#e2e8f0" : "#0f2557", border: "none", borderRadius: 8, padding: "5px 10px", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <div style={{ background: "linear-gradient(135deg,#0f2557,#2563eb)", borderRadius: 10, padding: "6px 16px", display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 20, fontWeight: 900, color: "#fde68a" }}>{total}</span>
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>total entries in {MONTH_NAMES[selMonth]} {selYear}</span>
        </div>
      </div>

      {total === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>📭</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>No entries for {MONTH_NAMES[selMonth]} {selYear}</div>
        </div>
      ) : (
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData.filter(d => d.count > 0)} margin={{ top: 4, right: 4, left: -20, bottom: 60 }} barSize={18}>
              <CartesianGrid strokeDasharray="3 3" stroke={d ? "rgba(255,255,255,0.06)" : "#f1f5f9"} vertical={false} />
              <XAxis dataKey="shortName" tick={{ fontSize: 13, fontWeight: 700, fill: d ? "#94a3b8" : "#64748b" }} angle={-45} textAnchor="end" interval={0} />
              <YAxis tick={{ fontSize: 13, fill: d ? "#94a3b8" : "#64748b" }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(37,99,235,0.06)" }} />
              <Bar dataKey="count" radius={6} fill="#2563eb" isAnimationActive={false}>
                {chartData.filter(d => d.count > 0).map((entry, i) => (
                  <Cell key={`cell-${i}`} fill={entry.color || "#2563eb"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {total > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
          {chartData.filter(x => x.count > 0).map((x, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: x.color + "18", border: `1px solid ${x.color}40`, borderRadius: 20, padding: "2px 10px", fontSize: 13, fontWeight: 700, color: x.color }}>
              {x.icon} {x.shortName} <span style={{ background: x.color, color: "#fff", borderRadius: 10, padding: "0px 5px", fontSize: 13 }}>{x.count}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function StarPerformerWidget({ facultyList, allSections, darkMode: d }) {
  const withTotals = useMemo(() => {
    return facultyList.map(f => {
      let total = 0;
      allSections.forEach(sec => {
        total += getFacultyCount(f, sec.key);
      });
      return { ...f, total };
    }).sort((a, b) => b.total - a.total);
  }, [facultyList, allSections]);

  const overallTop = withTotals[0];

  return (
    <div style={{ background: d ? "rgba(255,255,255,0.04)" : "#fff", borderRadius: 20, padding: "22px 24px", boxShadow: "0 4px 24px rgba(37,99,235,0.08)", border: `1px solid ${d ? "rgba(255,255,255,0.08)" : "#e8f0fe"}`, height: "100%", boxSizing: "border-box" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 22 }}>⭐</span>
        <div>
          <div style={{ fontSize: 16, fontWeight: 900, color: d ? "#e2e8f0" : "#0f2557" }}>Star Performer</div>
          <div style={{ fontSize: 13, color: "#94a3b8" }}>Highest overall contributions</div>
        </div>
      </div>

      {overallTop?.total > 0 ? (
        <>
          <div style={{ background: "linear-gradient(135deg, #0f2557, #2563eb, #7c3aed)", borderRadius: 16, padding: "20px", marginBottom: 14, position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", right: -10, bottom: -10, fontSize: 72, opacity: 0.07 }}>🏆</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <div style={{ width: 54, height: 54, borderRadius: "50%", background: "linear-gradient(135deg, #f7a84f, #f74f6e)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 900, border: "3px solid rgba(255,255,255,0.4)", flexShrink: 0 }}>
                {overallTop.name?.[0]?.toUpperCase()}
              </div>
              <div>
                <div style={{ background: "rgba(255,215,0,0.25)", border: "1px solid rgba(255,215,0,0.4)", borderRadius: 20, padding: "1px 10px", fontSize: 13, fontWeight: 800, color: "#fde68a", display: "inline-block", marginBottom: 4 }}>⭐ STAR PERFORMER</div>
                <div style={{ fontSize: 19, fontWeight: 900, color: "#fff" }}>{overallTop.name}</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }}>{overallTop.designation}</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>{overallTop.department}</div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 4 }}>Breakdown</div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {allSections.map(sec => {
                    const cnt = getFacultyCount(overallTop, sec.key);
                    return cnt > 0 ? (
                      <span key={sec.key} style={{ background: "rgba(255,255,255,0.15)", borderRadius: 6, padding: "2px 7px", fontSize: 13, color: "#fff", fontWeight: 600 }}>
                        {sec.icon} {cnt}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 38, fontWeight: 900, color: "#fde68a", lineHeight: 1 }}>{overallTop.total}</div>
                <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)" }}>Total Entries</div>
              </div>
            </div>
          </div>

          <div style={{ fontSize: 13, fontWeight: 800, color: "#94a3b8", letterSpacing: 1, marginBottom: 8 }}>🏅 TOP 3 OVERALL</div>
          {withTotals.slice(0, 3).map((f, i) => (
            <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 10, marginBottom: 6, background: d ? "rgba(255,255,255,0.03)" : i === 0 ? "#fffbeb" : "#f8fafc", border: `1px solid ${i === 0 ? "#fde68a" : "#f1f5f9"}` }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{["🥇", "🥈", "🥉"][i]}</span>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: `linear-gradient(135deg, #f7a84f, #f74f6e)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900, color: "#fff", flexShrink: 0 }}>
                {f.name?.[0]?.toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: d ? "#e2e8f0" : "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.name}</div>
                <div style={{ fontSize: 13, color: "#94a3b8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.department}</div>
              </div>
              <div style={{ background: i === 0 ? "#f59e0b" : "#e2e8f0", color: i === 0 ? "#fff" : "#475569", borderRadius: 8, padding: "2px 8px", fontSize: 14, fontWeight: 900, flexShrink: 0 }}>{f.total}</div>
            </div>
          ))}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>No data yet</div>
      )}
    </div>
  );
}
